/**
 * React Query hook for VLP quotes.
 * Provides real-time bid/ask prices from the Virtual Liquidity Pool.
 * Uses dedicated VLP WebSocket channel for real-time updates with HTTP polling fallback.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { VlpApi } from "../api-client";
import type { VlpQuote } from "../types";
import { parseDecimal } from "../types";
import { useWsVlpQuote } from "./use-ws-subscription";
import { type GenerationConfig, DEFAULT_ORDER_BOOK_CONFIG } from "./use-order-book-config";

/**
 * Hook to fetch all VLP quotes
 * Reduced polling frequency - use WebSocket for real-time data
 */
export const useVlpQuotes = (refetchInterval = 5000) => {
  return useQuery({
    queryKey: ["vlp-quotes"],
    queryFn: () => VlpApi.getQuotes(),
    refetchInterval,
    staleTime: 2000,
  });
};

/**
 * Hook to fetch VLP quote for a specific market
 * Uses dedicated VLP WebSocket channel for real-time bid/ask updates, HTTP polling as fallback
 */
export const useVlpQuote = (market: string | undefined, refetchInterval = 5000) => {
  // Subscribe to VLP WebSocket channel for real-time bid/ask prices
  const wsVlp = useWsVlpQuote(market);
  
  const {
    data: quote,
    isLoading: httpLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["vlp-quote", market],
    queryFn: () => (market ? VlpApi.getQuote(market) : null),
    // FIX: Always fetch HTTP initially, only disable after WebSocket has data
    // This prevents race condition where WS connects fast but has no data yet
    enabled: !!market && (!wsVlp.isConnected || !wsVlp.quote),
    refetchInterval: (wsVlp.isConnected && wsVlp.quote) ? false : refetchInterval, // Only stop polling when WS has data
    staleTime: 2000,
  });
  
  // Parse HTTP quote data to numbers (fallback)
  const httpQuote = useMemo(() => {
    if (!quote) return null;
    return {
      market: quote.market,
      bidPrice: parseDecimal(quote.bid_price),
      askPrice: parseDecimal(quote.ask_price),
      spread: parseDecimal(quote.spread),
      oraclePrice: parseDecimal(quote.oracle_price),
      vlpPosition: parseDecimal(quote.vlp_position),
      timestamp: new Date(quote.timestamp),
    };
  }, [quote]);

  // Use WebSocket data if available, otherwise fall back to HTTP data
  // The WebSocket hook now properly filters by message type, so data is always valid
  const parsedQuote = useMemo(() => {
    // Prefer WebSocket data when connected and available
    if (wsVlp.isConnected && wsVlp.quote) {
      return wsVlp.quote;
    }
    // Fall back to HTTP data
    return httpQuote;
  }, [wsVlp.isConnected, wsVlp.quote, httpQuote]);

  // Create a "raw quote" that matches the API format for synthetic orderbook
  const rawQuote = useMemo(() => {
    // FIX: Always return null instead of undefined to prevent orderbook from hanging
    if (!parsedQuote) return quote ?? null;
    
    // If using WebSocket data, create the raw format
    if (wsVlp.isConnected && wsVlp.quote) {
      return {
        market: parsedQuote.market,
        bid_price: parsedQuote.bidPrice.toString(),
        ask_price: parsedQuote.askPrice.toString(),
        spread: parsedQuote.spread.toString(),
        oracle_price: parsedQuote.oraclePrice.toString(),
        vlp_position: parsedQuote.vlpPosition.toString(),
        timestamp: parsedQuote.timestamp.toISOString(),
      };
    }
    
    // Use HTTP quote directly, fallback to null
    return quote ?? null;
  }, [parsedQuote, wsVlp.isConnected, wsVlp.quote, quote]);

  // Calculate mid price and spread percentage
  const midPrice = parsedQuote
    ? (parsedQuote.bidPrice + parsedQuote.askPrice) / 2
    : 0;
  const spreadPercent = midPrice > 0 && parsedQuote
    ? (parsedQuote.spread / midPrice) * 100
    : 0;

  // FIX: Show loading state if we don't have any data yet (neither WS nor HTTP)
  // This prevents showing empty orderbook while waiting for first data
  const isLoading = !parsedQuote && httpLoading;

  return {
    quote: parsedQuote,
    rawQuote,
    midPrice,
    spread: parsedQuote?.spread ?? 0,
    spreadPercent,
    oraclePrice: parsedQuote?.oraclePrice ?? 0,
    isLoading,
    error: error as Error | null,
    refetch,
    // Expose WebSocket connection state
    isWsConnected: wsVlp.isConnected,
  };
};

/**
 * Generate synthetic order book levels from VLP quote.
 * Creates realistic-looking depth levels around the bid/ask.
 * 
 * PURE FUNCTION: Uses external timestamp for animations to enable proper React memoization.
 * Same inputs (including timestamp) = same output every time.
 * 
 * @param quote - VLP quote with bid/ask prices
 * @param levels - Number of levels to generate on each side
 * @param basePrecision - Decimal precision for amounts
 * @param quotePrecision - Decimal precision for prices
 * @param tickSize - Price step size (tick size from order book UI)
 * @param config - Optional generation config for animation parameters
 * @param currentPrice - Optional override price from frontend (Binance Spot ticker)
 * @param animationTimestamp - External clock for animations (controlled by parent component)
 */
export function generateSyntheticOrderBook(
  quote: VlpQuote | null,
  levels = 10,
  basePrecision = 8,
  quotePrecision = 2,
  tickSize = 0,
  config: GenerationConfig = DEFAULT_ORDER_BOOK_CONFIG.generation,
  currentPrice?: number,
  animationTimestamp?: number
): {
  bids: Array<{ price: number; amount: number }>;
  asks: Array<{ price: number; amount: number }>;
} {
  if (!quote) {
    return { bids: [], asks: [] };
  }

  // Use currentPrice override if provided (frontend's Binance Spot ticker)
  // This ensures order book matches the header price exactly
  const centerPrice = currentPrice ?? parseDecimal(quote.oracle_price);
  const spread = parseDecimal(quote.spread);
  const halfSpread = spread / 2;
  
  const bidPrice = currentPrice ? centerPrice - halfSpread : parseDecimal(quote.bid_price);
  const askPrice = currentPrice ? centerPrice + halfSpread : parseDecimal(quote.ask_price);
  const oraclePrice = centerPrice;

  // Use tick size as price step if provided, otherwise calculate based on price magnitude
  const defaultStep = calculatePriceStep(oraclePrice, quotePrecision);
  const priceStep = tickSize > 0 ? tickSize : defaultStep;
  
  // Calculate tick multiplier - amounts should scale with tick size
  // When tick size is larger, more orders are aggregated into each level
  const tickMultiplier = tickSize > 0 ? Math.sqrt(tickSize / defaultStep) : 1;
  
  // Animation speed factor - larger tick sizes = slower movement
  const animationSpeedFactor = tickSize > 0 ? Math.max(0.1, defaultStep / tickSize) : 1;

  const bids: Array<{ price: number; amount: number }> = [];
  const asks: Array<{ price: number; amount: number }> = [];

  // IMPORTANT: First level uses ACTUAL VLP prices (preserves real spread)
  // Subsequent levels use tick-aligned prices for display
  
  // Generate bid levels (descending from best bid)
  for (let i = 0; i < levels; i++) {
    let price: number;
    if (i === 0) {
      // First level: use actual VLP bid price to preserve spread
      price = bidPrice;
    } else {
      // Subsequent levels: align to tick size grid
      const roundedBid = tickSize > 0 
        ? Math.floor(bidPrice / tickSize) * tickSize 
        : bidPrice;
      price = roundedBid - i * priceStep;
    }
    
    if (price <= 0) break;
    
    const amount = generateRealisticAmount(
      oraclePrice,
      price,
      i,
      levels,
      tickMultiplier,
      animationSpeedFactor,
      "bid",
      config,
      animationTimestamp
    );
    
    bids.push({
      price: roundToDecimals(price, quotePrecision),
      amount: roundToDecimals(amount, basePrecision),
    });
  }

  // Generate ask levels (ascending from best ask)
  for (let i = 0; i < levels; i++) {
    let price: number;
    if (i === 0) {
      // First level: use actual VLP ask price to preserve spread
      price = askPrice;
    } else {
      // Subsequent levels: align to tick size grid
      const roundedAsk = tickSize > 0 
        ? Math.ceil(askPrice / tickSize) * tickSize 
        : askPrice;
      price = roundedAsk + i * priceStep;
    }
    
    const amount = generateRealisticAmount(
      oraclePrice,
      price,
      i,
      levels,
      tickMultiplier,
      animationSpeedFactor,
      "ask",
      config,
      animationTimestamp
    );
    
    asks.push({
      price: roundToDecimals(price, quotePrecision),
      amount: roundToDecimals(amount, basePrecision),
    });
  }

  return { bids, asks };
}

/**
 * Generate a realistic amount for an order book level.
 * PURE FUNCTION - uses external timestamp for deterministic animation.
 */
function generateRealisticAmount(
  oraclePrice: number,
  levelPrice: number,
  levelIndex: number,
  _totalLevels: number,
  tickMultiplier: number,
  animationSpeedFactor: number,
  side: "bid" | "ask",
  config: GenerationConfig,
  timestamp?: number
): number {
  // Base amount depends on asset price (smaller for expensive assets)
  const baseAmount = generateBaseAmount(oraclePrice);
  
  // Depth curve: more liquidity near best price, decreasing further away
  // Uses configurable depth curve factor (default 0.12)
  const depthFactor = Math.exp(-levelIndex * config.depth_curve_factor);
  
  // Get dynamic variation using EXTERNAL timestamp (makes function pure)
  const dynamicVariation = config.enabled && timestamp
    ? getDynamicVariation(levelPrice, levelIndex, side, animationSpeedFactor, config, timestamp)
    : 1.0; // Static if disabled or no timestamp
  
  // Round number bonus: add extra liquidity at round numbers (walls)
  const roundNumberBonus = getRoundNumberBonus(levelPrice, oraclePrice, config);
  
  // Increase tick multiplier effect for larger amounts
  const enhancedTickMultiplier = tickMultiplier > 1 ? tickMultiplier * 1.5 : tickMultiplier;
  
  // Combine all factors
  const amount = baseAmount * enhancedTickMultiplier * depthFactor * dynamicVariation * roundNumberBonus;
  
  return amount;
}

/**
 * Create dynamic variation that makes the order book feel alive.
 * PURE FUNCTION - uses external timestamp instead of Date.now().
 * This allows proper React memoization while maintaining animation.
 */
function getDynamicVariation(
  price: number, 
  levelIndex: number, 
  side: "bid" | "ask",
  speedFactor: number,
  config: GenerationConfig,
  now: number  // External timestamp - makes function pure!
): number {
  // Stable base component (so each level has its own "personality")
  const priceHash = hashPrice(price, side);
  const variationRange = config.base_variation_max - config.base_variation_min;
  const baseVariation = config.base_variation_min + (priceHash * variationRange);
  
  // Scale wave amplitudes by speed factor (smaller at large tick sizes)
  const waveScale = Math.sqrt(speedFactor);
  
  // Fast fluctuation - changes every ~400ms at default
  const fastPeriod = config.fast_wave_period_ms / speedFactor;
  const fastWave = Math.sin(now / fastPeriod + price * 0.1 + levelIndex * 2) * config.fast_wave_amplitude * waveScale;
  
  // Medium fluctuation - changes every ~1.8s, creates larger swings
  const mediumPeriod = config.medium_wave_period_ms / speedFactor;
  const mediumWave = Math.sin(now / mediumPeriod + price * 0.05) * config.medium_wave_amplitude * waveScale;
  
  // Slow drift - changes every ~5s, overall trend
  const slowPeriod = config.slow_wave_period_ms / speedFactor;
  const slowWave = Math.sin(now / slowPeriod + price * 0.02 + (side === "bid" ? 0 : Math.PI)) * config.slow_wave_amplitude * waveScale;
  
  // Deterministic "jitter" based on timestamp and price (not Math.random()!)
  // Creates micro-fluctuations without breaking function purity
  const jitterSeed = (now / 137) + price * levelIndex;
  const jitter = Math.sin(jitterSeed * 9.87) * config.jitter_amplitude * waveScale;
  
  // Combine: base ± waves ± jitter
  const totalVariation = baseVariation + fastWave + mediumWave + slowWave + jitter;
  
  // Clamp to reasonable range (0.5 to 1.5)
  return Math.max(0.5, Math.min(1.5, totalVariation));
}

/**
 * Create a deterministic hash from price for stable base variation.
 */
function hashPrice(price: number, side: "bid" | "ask"): number {
  const priceStr = price.toFixed(2);
  let hash = side === "bid" ? 0.3 : 0.6;
  
  for (let i = 0; i < priceStr.length; i++) {
    const char = priceStr.charCodeAt(i);
    hash = ((hash * 31) + char) % 1;
  }
  
  return hash;
}

/**
 * Add bonus liquidity at psychologically significant round numbers.
 * This creates realistic "walls" in the order book.
 */
function getRoundNumberBonus(price: number, oraclePrice: number, config: GenerationConfig): number {
  // Determine round number thresholds based on price magnitude
  let majorRound: number;
  let minorRound: number;
  
  if (oraclePrice >= 10000) {
    majorRound = 1000; // BTC: walls at $90,000, $91,000, etc.
    minorRound = 100;  // Minor walls at $90,100, $90,200, etc.
  } else if (oraclePrice >= 1000) {
    majorRound = 100;  // ETH: walls at $3,000, $3,100, etc.
    minorRound = 10;
  } else if (oraclePrice >= 100) {
    majorRound = 10;   // SOL: walls at $150, $160, etc.
    minorRound = 1;
  } else {
    majorRound = 1;
    minorRound = 0.1;
  }
  
  // Check if price is at a round number
  // Use configurable bonus multipliers
  if (Math.abs(price % majorRound) < 0.01) {
    return config.round_number_bonus_major; // Major wall: configurable liquidity
  }
  if (Math.abs(price % minorRound) < 0.01) {
    return config.round_number_bonus_minor; // Minor wall: configurable liquidity
  }
  
  return 1.0; // Normal level
}

/**
 * Calculate appropriate price step based on asset price
 */
function calculatePriceStep(price: number, precision: number): number {
  if (price >= 10000) return 1; // BTC-like: $1 steps
  if (price >= 1000) return 0.1; // ETH-like: $0.10 steps
  if (price >= 100) return 0.01; // SOL-like: $0.01 steps
  if (price >= 10) return 0.001;
  if (price >= 1) return 0.0001;
  return Math.pow(10, -precision);
}

/**
 * Generate base amount based on asset price (smaller amounts for higher prices)
 * Amounts are set to show healthy trading volume (~$25k-$75k per level)
 * Covers all trading pairs: BTC, ETH, SOL, BNB, LTC, DOT, XRP, ADA, DOGE, MATIC
 */
function generateBaseAmount(price: number): number {
  if (price >= 10000) return 2.5;    // BTC ($90k): ~$225k/level
  if (price >= 1000) return 25;      // ETH ($3k): ~$75k/level
  if (price >= 500) return 75;       // BNB ($600): ~$45k/level
  if (price >= 100) return 250;      // SOL ($150), LTC ($100): ~$25-37k/level
  if (price >= 5) return 5000;       // DOT ($7): ~$35k/level
  if (price >= 0.5) return 50000;    // MATIC ($0.80), XRP ($0.50): ~$25-40k/level
  return 100000;                      // ADA ($0.40), DOGE ($0.10): ~$10-40k/level
}

/**
 * Round to specified decimal places
 */
function roundToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
